import { useState } from "react";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { ChevronRight, ChevronLeft, User, Briefcase, CheckCircle, Loader2 } from "lucide-react";
import { useMutation, useApolloClient } from "@apollo/client/react";
import { COMPLETE_INDIVIDUAL_ONBOARDING, COMPLETE_EMAIL_ONBOARDING } from "@/graphql/mutations";
import { useNavigate } from "react-router-dom";
import { useToast } from "@/hooks/use-toast";
import { saveTokens } from "@/lib/token-utils";
import { getStoredDashboardAttribution } from "@/lib/analytics";

interface IndividualOnboardingProps {
  step: number;
  onNext: () => void;
  onBack: () => void;
  onComplete: (data: IndividualData) => void;
  apolloClient: ReturnType<typeof useApolloClient>;
}

interface IndividualData {
  fullName: string;
  profession: string;
  customProfession: string;
  organization: string;
  useCase: string;
  customUseCase: string;
  expectedUsage: string;
  goals: string;
  hearAboutUs: string;
  customHearAboutUs: string;
}

const IndividualOnboarding = ({ step, onNext, onBack, onComplete, apolloClient }: IndividualOnboardingProps) => {
  const navigate = useNavigate();
  const { toast } = useToast();
  const [individualData, setIndividualData] = useState<IndividualData>({
    fullName: "",
    profession: "",
    customProfession: "",
    organization: "",
    useCase: "",
    customUseCase: "",
    expectedUsage: "",
    goals: "",
    hearAboutUs: "",
    customHearAboutUs: "",
  });

  const handleOnboardingSuccess = async (result: { success: boolean; error?: string; tokens?: { accessToken: string; refreshToken: string } }) => {
    if (!result.success) {
      toast({
        title: "Error",
        description: result.error || "Failed to complete onboarding. Please try again.",
        variant: "destructive",
      });
      return;
    }

    if (result.tokens) {
      saveTokens(result.tokens.accessToken, result.tokens.refreshToken);
      await apolloClient.resetStore();
    }

    sessionStorage.removeItem('pendingSignupSource');
    // Redirect to dashboard
    window.location.href = "/";
  };

  const handleOnboardingError = (error: Error) => {
    console.error("Onboarding error:", error);
    toast({
      title: "Error",
      description: error.message || "Failed to complete onboarding. Please try again.",
      variant: "destructive",
    });
  };

  const [completeOnboarding, { loading: isCompletingGoogle }] = useMutation(COMPLETE_INDIVIDUAL_ONBOARDING, {
    onCompleted: (data: { completeIndividualOnboarding: { success: boolean; error?: string; tokens?: { accessToken: string; refreshToken: string } } }) => handleOnboardingSuccess(data.completeIndividualOnboarding),
    onError: handleOnboardingError,
  });

  const [completeEmailOnboarding, { loading: isCompletingEmail }] = useMutation(COMPLETE_EMAIL_ONBOARDING, {
    onCompleted: (data: { completeEmailOnboarding: { success: boolean; error?: string; tokens?: { accessToken: string; refreshToken: string } } }) => handleOnboardingSuccess(data.completeEmailOnboarding),
    onError: handleOnboardingError,
  });

  const isCompleting = isCompletingGoogle || isCompletingEmail;

  const isStepValid = () => {
    switch (step) {
      case 1:
        if (!individualData.fullName) return false;
        if (individualData.profession === "other") {
          return !!individualData.customProfession.trim();
        }
        return !!individualData.profession;
      case 2:
        if (!individualData.expectedUsage) return false;
        if (!individualData.hearAboutUs) return false;
        if (individualData.hearAboutUs === "other" && !individualData.customHearAboutUs.trim()) return false;
        if (individualData.useCase === "other") {
          return !!individualData.customUseCase.trim();
        }
        return !!individualData.useCase;
      default:
        return false;
    }
  };

  const handleNext = async () => {
    if (step === 2) {
      // Last step - complete onboarding
      const googleIdToken = sessionStorage.getItem('pendingGoogleIdToken');
      const pendingEmail = sessionStorage.getItem('pendingEmailSignup');

      if (!googleIdToken && !pendingEmail) {
        toast({
          title: "Error",
          description: "Authentication not found. Redirecting to sign in...",
          variant: "destructive",
        });
        setTimeout(() => {
          navigate("/login");
        }, 1500);
        return;
      }

      // Generate organization name from user's name or use a default
      const orgName = individualData.fullName 
        ? `${individualData.fullName}'s Workspace`
        : "My Workspace";

      // Prepare form data
      const formData = {
        fullName: individualData.fullName,
        profession: individualData.profession === "other" 
          ? individualData.customProfession 
          : individualData.profession,
        organization: individualData.organization,
        useCase: individualData.useCase === "other"
          ? individualData.customUseCase
          : individualData.useCase,
        expectedUsage: individualData.expectedUsage,
        goals: individualData.goals,
        hearAboutUs: (individualData.hearAboutUs === "other"
          ? individualData.customHearAboutUs.trim()
          : individualData.hearAboutUs),
        attribution: getStoredDashboardAttribution(),
      };

      try {
        if (googleIdToken) {
          // Google SSO user
          await completeOnboarding({
            variables: {
              input: {
                organizationName: orgName,
                formData: formData,
                googleIdToken: googleIdToken,
              },
            },
          });
          sessionStorage.removeItem('pendingGoogleIdToken');
        } else if (pendingEmail) {
          // Email signup user
          await completeEmailOnboarding({
            variables: {
              input: {
                email: pendingEmail,
                organizationName: orgName,
                formData: formData,
              },
            },
          });
          sessionStorage.removeItem('pendingEmailSignup');
        }
      } catch (error) {
        // Error handled by onError callback
      }
    } else {
      onNext();
    }
  };

  return (
    <>
      {/* Step 1: Personal Details */}
      {step === 1 && (
        <Card className="p-8 border-border shadow-xl animate-fade-in">
          <div className="space-y-6">
            <div className="flex items-center gap-3">
              <div className="w-12 h-12 rounded-xl bg-primary/10 flex items-center justify-center">
                <User className="w-6 h-6 text-primary" />
              </div>
              <div>
                <h2 className="text-2xl font-bold">Personal Information</h2>
                <p className="text-muted-foreground">Tell us about yourself</p>
              </div>
            </div>

            <div className="space-y-4 pt-4">
              <div className="space-y-2">
                <Label htmlFor="full-name">Full Name *</Label>
                <Input
                  id="full-name"
                  placeholder="John Doe"
                  value={individualData.fullName}
                  onChange={(e) =>
                    setIndividualData({ ...individualData, fullName: e.target.value })
                  }
                  className="h-12"
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="profession">Profession / Role *</Label>
                <Select
                  value={individualData.profession}
                  onValueChange={(value) =>
                    setIndividualData({ ...individualData, profession: value, customProfession: value === "other" ? individualData.customProfession : "" })
                  }
                >
                  <SelectTrigger id="profession" className="h-12">
                    <SelectValue placeholder="Select your profession" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="developer">Software Developer</SelectItem>
                    <SelectItem value="designer">Designer</SelectItem>
                    <SelectItem value="content-creator">Content Creator</SelectItem>
                    <SelectItem value="educator">Educator / Teacher</SelectItem>
                    <SelectItem value="consultant">Consultant</SelectItem>
                    <SelectItem value="freelancer">Freelancer</SelectItem>
                    <SelectItem value="entrepreneur">Entrepreneur</SelectItem>
                    <SelectItem value="student">Student</SelectItem>
                    <SelectItem value="researcher">Researcher</SelectItem>
                    <SelectItem value="marketing">Marketing Professional</SelectItem>
                    <SelectItem value="sales">Sales Professional</SelectItem>
                    <SelectItem value="other">Other</SelectItem>
                  </SelectContent>
                </Select>
                {individualData.profession === "other" && (
                  <div className="mt-2">
                    <Input
                      id="custom-profession"
                      placeholder="Please specify your profession"
                      value={individualData.customProfession}
                      onChange={(e) =>
                        setIndividualData({ ...individualData, customProfession: e.target.value })
                      }
                      className="h-12"
                    />
                  </div>
                )}
              </div>

              <div className="space-y-2">
                <Label htmlFor="organization">Current Organization (Optional)</Label>
                <Input
                  id="organization"
                  placeholder="Company or institution name"
                  value={individualData.organization}
                  onChange={(e) =>
                    setIndividualData({ ...individualData, organization: e.target.value })
                  }
                  className="h-12"
                />
                <p className="text-xs text-muted-foreground">
                  Let us know if you're affiliated with any organization
                </p>
              </div>
            </div>

            <div className="flex justify-between pt-6">
              <Button variant="outline" onClick={onBack} className="gap-2">
                <ChevronLeft className="w-4 h-4" />
                Back
              </Button>
              <Button onClick={onNext} disabled={!isStepValid()} className="gap-2">
                Continue
                <ChevronRight className="w-4 h-4" />
              </Button>
            </div>
          </div>
        </Card>
      )}

      {/* Step 2: Usage & Goals */}
      {step === 2 && (
        <Card className="p-8 border-border shadow-xl animate-fade-in">
          <div className="space-y-6">
            <div className="flex items-center gap-3">
              <div className="w-12 h-12 rounded-xl bg-primary/10 flex items-center justify-center">
                <Briefcase className="w-6 h-6 text-primary" />
              </div>
              <div>
                <h2 className="text-2xl font-bold">Usage & Goals</h2>
                <p className="text-muted-foreground">Help us personalize your experience</p>
              </div>
            </div>

            <div className="space-y-4 pt-4">
              <div className="space-y-2">
                <Label htmlFor="use-case">What will you use FlowLabs for? *</Label>
                <Select
                  value={individualData.useCase}
                  onValueChange={(value) =>
                    setIndividualData({ ...individualData, useCase: value, customUseCase: value === "other" ? individualData.customUseCase : "" })
                  }
                >
                  <SelectTrigger id="use-case" className="h-12">
                    <SelectValue placeholder="Select your primary use case" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="personal-projects">Personal Projects</SelectItem>
                    <SelectItem value="client-work">Client Work / Freelancing</SelectItem>
                    <SelectItem value="streaming">Live Streaming</SelectItem>
                    <SelectItem value="meetings">Video Meetings</SelectItem>
                    <SelectItem value="webinars">Webinars / Presentations</SelectItem>
                    <SelectItem value="tutorials">Creating Tutorials</SelectItem>
                    <SelectItem value="collaboration">Remote Collaboration</SelectItem>
                    <SelectItem value="testing">Testing / Evaluation</SelectItem>
                    <SelectItem value="other">Other</SelectItem>
                  </SelectContent>
                </Select>
                {individualData.useCase === "other" && (
                  <div className="mt-2">
                    <Input
                      id="custom-use-case"
                      placeholder="Please specify your use case"
                      value={individualData.customUseCase}
                      onChange={(e) =>
                        setIndividualData({ ...individualData, customUseCase: e.target.value })
                      }
                      className="h-12"
                    />
                  </div>
                )}
              </div>

              <div className="space-y-2">
                <Label htmlFor="expected-usage">Expected Monthly Usage *</Label>
                <Select
                  value={individualData.expectedUsage}
                  onValueChange={(value) =>
                    setIndividualData({ ...individualData, expectedUsage: value })
                  }
                >
                  <SelectTrigger id="expected-usage" className="h-12">
                    <SelectValue placeholder="Select expected usage" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="occasional">Occasional (1-5 sessions)</SelectItem>
                    <SelectItem value="regular">Regular (6-15 sessions)</SelectItem>
                    <SelectItem value="frequent">Frequent (16-30 sessions)</SelectItem>
                    <SelectItem value="heavy">Heavy (30+ sessions)</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              <div className="space-y-2">
                <Label htmlFor="hear-about-us">How did you hear about Flow? *</Label>
                <Select
                  value={individualData.hearAboutUs}
                  onValueChange={(value) =>
                    setIndividualData({ ...individualData, hearAboutUs: value, customHearAboutUs: value === "other" ? individualData.customHearAboutUs : "" })
                  }
                >
                  <SelectTrigger id="hear-about-us" className="h-12">
                    <SelectValue placeholder="Select an option" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="search">Search engine (Google, Bing...)</SelectItem>
                    <SelectItem value="reddit">Reddit</SelectItem>
                    <SelectItem value="forum">Forum (Lift Gamma Gain, Blackmagic...)</SelectItem>
                    <SelectItem value="youtube">YouTube</SelectItem>
                    <SelectItem value="social">Social media</SelectItem>
                    <SelectItem value="word-of-mouth">Friend or colleague</SelectItem>
                    <SelectItem value="email">Email from FlowLabs</SelectItem>
                    <SelectItem value="other">Other</SelectItem>
                  </SelectContent>
                </Select>
                {individualData.hearAboutUs === "other" && (
                  <div className="mt-2">
                    <Input
                      id="custom-hear-about-us"
                      placeholder="Please tell us where"
                      maxLength={200}
                      required
                      value={individualData.customHearAboutUs}
                      onChange={(e) =>
                        setIndividualData({ ...individualData, customHearAboutUs: e.target.value })
                      }
                      className="h-12"
                    />
                  </div>
                )}
              </div>

              <div className="space-y-2">
                <Label htmlFor="goals">What are your goals with FlowLabs? (Optional)</Label>
                <Textarea
                  id="goals"
                  placeholder="e.g., I want to create high-quality tutorial videos, host virtual workshops..."
                  value={individualData.goals}
                  onChange={(e) =>
                    setIndividualData({ ...individualData, goals: e.target.value })
                  }
                  className="min-h-[100px] resize-none"
                />
                <p className="text-xs text-muted-foreground">
                  This helps us provide you with relevant tips and features
                </p>
              </div>
            </div>

            <div className="flex justify-between pt-6">
              <Button variant="outline" onClick={onBack} className="gap-2">
                <ChevronLeft className="w-4 h-4" />
                Back
              </Button>
              <Button 
                onClick={handleNext} 
                disabled={!isStepValid() || isCompleting} 
                className="gap-2 bg-green-600 hover:bg-green-700"
              >
                {isCompleting ? (
                  <>
                    <Loader2 className="w-4 h-4 animate-spin" />
                    Completing...
                  </>
                ) : (
                  <>
                    <CheckCircle className="w-4 h-4" />
                    Complete Setup
                  </>
                )}
              </Button>
            </div>
          </div>
        </Card>
      )}
    </>
  );
};

export default IndividualOnboarding;
